import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { SessionRecordFull, SessionState } from "../backend/Api";
import { cumulativeLengths, drawPreviewBase64, MAX_POINTS, MIN_POINTS, normalizePoints, Point, rdp, resampleToN, sampleByDistance, TARGET_NORMALIZED_DIST } from "./editorUtils";
import { useBackendContext, useBackendContextMessages } from "../backend/backendContextProvider";
import { useTranslation } from "react-i18next";
import { T } from "../../i18n/LocaleType";
import { useRouterContext } from "../router/routerContext";
import { AvailableColor, ColorPalettes, PublicColorKey } from "./ColorPalette";

type StrokeState = {
    rawPoints: Point[];
    previewPoints: Point[];
    finished: boolean;
    processedNormalized: Point[];
    processedBase64: string | undefined;
    color?: PublicColorKey,
    availableColors: {[key in PublicColorKey]: string};
    comment?: string
}

type StrokeActions = {
    startStroke: (point: Point) => void;
    addPoint: (point: Point) => void;
    endStroke: () => void;
    clear: () => void;
    restore: (session: SessionRecordFull) => void;
    setColor: (value: PublicColorKey | undefined) => void;
    setComment: (value: string) => void;
    setCanvasSize: (size: number) => void;
}

const StrokeContext = createContext<StrokeState & StrokeActions | undefined>(undefined);

export const StrokeProvider: FC<PropsWithChildren> = ({ children }) => {

    const id = useId();

    const { t } = useTranslation();

    const { service } = useBackendContext();



    const { addMessage, clearAllMessages } = useBackendContextMessages();

    const { showEditor, showStage } = useRouterContext();

    const [rawPoints, setRawPoints] = useState<Point[]>([]);
    const [previewPoints, setPreviewPoints] = useState<Point[]>([]);
    const [finished, setFinished] = useState<boolean>(false);
    const [processedNormalized, setProcessedNormalized] = useState<Point[]>([]);
    const [processedBase64, setProcessedBase64] = useState<string | undefined>(undefined);
    const [color, setColor] = useState<PublicColorKey | undefined>(undefined);
    const [comment, setComment] = useState<string | undefined>(undefined);
    const cumulativeLengthRef = useRef<number>(0);

    const availableColors = useMemo(() => {

        return ColorPalettes.public.mapToCssFormat();

    }, []);

    const canvasSizeRef = useRef<number>(500);

    const setCanvasSize = useCallback((size: number) => {
        canvasSizeRef.current = size;
    }, []);

    const processPoints = useCallback((
        points: Point[]
    ) => {

        const sampled = sampleByDistance(points);
        const simplified = rdp(sampled);

        // Calculate total path length in pixel space → normalized
        const lens = cumulativeLengths(simplified);
        const totalPixelLen = lens[lens.length - 1];
        const totalNormLen = totalPixelLen / canvasSizeRef.current;

        // Compute dynamic point count based on target inter-point distance
        const rawN = Math.round(totalNormLen / TARGET_NORMALIZED_DIST) + 1;
        const n = Math.max(2, Math.min(MAX_POINTS, rawN));
        const isTooShort = rawN < MIN_POINTS;

        const resampled = resampleToN(simplified, n);
        const normalized = normalizePoints(resampled, canvasSizeRef.current);
        const base64 = drawPreviewBase64(normalized, 6, 200);

        return {
            normalized,
            base64,
            resampled,
            tooShort: isTooShort
        }

    }, []);

    const denormalizePoints = useCallback((normalized: Point[]) => {
        return normalized.map(p => ({ x: p.x * canvasSizeRef.current, y: p.y * canvasSizeRef.current }));
    }, []);


    const finalizeProcessing = useCallback(() => {

        // Recompute and replace previewPoints with processed version (so user sees exactly what will be sent)
        setRawPoints(prev => {
            if (!prev || prev.length === 0) return prev;
            const result = processPoints(prev);

            console.log(`[Stroke] points: ${result.normalized.length}, tooShort: ${result.tooShort}`);

            if (result.tooShort) {
                // Line is too short — notify and clear
                addMessage(t(T.too_short_notice), true, 4000);
                setPreviewPoints([]);
                setProcessedNormalized([]);
                setProcessedBase64(undefined);
                setFinished(false);
                cumulativeLengthRef.current = 0;
                return [];
            }

            // Bounding box check — both dimensions must span ≥ 2/3 of canvas
            const xs = result.normalized.map(p => p.x);
            const ys = result.normalized.map(p => p.y);
            const bboxW = Math.max(...xs) - Math.min(...xs);
            const bboxH = Math.max(...ys) - Math.min(...ys);
            const MIN_BBOX = 1 / 2;
            if (bboxW < MIN_BBOX || bboxH < MIN_BBOX) {
                addMessage(t(T.too_small_notice), true, 4000);
                setPreviewPoints([]);
                setProcessedNormalized([]);
                setProcessedBase64(undefined);
                setFinished(false);
                cumulativeLengthRef.current = 0;
                return [];
            }

            // previewPoints should be in pixel coords (resampled) so UI draws them correctly
            setPreviewPoints(result.resampled);
            setProcessedNormalized(result.normalized);
            setProcessedBase64(result.base64);
            // also update rawPoints to the resampled array -> consistent state
            return result.resampled;
        });

    }, [processPoints, addMessage, t]);


    const endStroke = useCallback(() => {

        setFinished(true);

        finalizeProcessing();

    }, [setFinished, finalizeProcessing]);



    const startStroke = useCallback((
        p: Point
    ) => {
        setRawPoints([p]);
        setPreviewPoints([p]);
        setFinished(false);
        setProcessedNormalized([]);
        setProcessedBase64(undefined);
        cumulativeLengthRef.current = 0;
        clearAllMessages();
    }, [setRawPoints, setPreviewPoints, setFinished, clearAllMessages]);

    const addPoint = useCallback((
        p: Point
    ) => {

        // Max cumulative path length in pixels that yields MAX_POINTS
        const maxPixelLen = (MAX_POINTS - 1) * TARGET_NORMALIZED_DIST * canvasSizeRef.current;

        setRawPoints(prev => {
            if (prev.length === 0) return prev;
            // Already at max length — reject
            if (cumulativeLengthRef.current >= maxPixelLen) return prev;

            const lastPt = prev[prev.length - 1];
            const segLen = Math.hypot(p.x - lastPt.x, p.y - lastPt.y);
            cumulativeLengthRef.current += segLen;

            const next = [...prev, p];
            setPreviewPoints(next);

            if (cumulativeLengthRef.current >= maxPixelLen) {
                setFinished(true);
                setTimeout(() => {
                    finalizeProcessing();
                }, 0);
            }
            return next;
        });

    }, [setPreviewPoints, setFinished, finalizeProcessing]);



    const clear = useCallback(() => {

        setRawPoints([]);
        setPreviewPoints([]);
        setFinished(false);
        setProcessedNormalized([]);
        setProcessedBase64(undefined);
        setColor(undefined);
        cumulativeLengthRef.current = 0;

    }, [setRawPoints, setPreviewPoints, setFinished, setColor]);

    const restore = useCallback((session: SessionRecordFull) => {

        const normalizedPoints = JSON.parse(session.line!);

        // If normalized points provided, denormalize first
        if (normalizedPoints && normalizedPoints.length > 0) {
            const denorm = denormalizePoints(normalizedPoints);
            // use resampled/processed points to keep consistent UI
            const result = processPoints(denorm);
            setRawPoints(result.resampled);
            setPreviewPoints(result.resampled);
            setProcessedNormalized(result.normalized); // normalized could be re-computed for precision
            setColor(session.color_a ?? undefined);
            setComment(session.comment ?? undefined);
            setProcessedBase64(session.image_data! ?? result.base64);
            setFinished(session.state !== SessionState.NEW);

            return;
        }

        // otherwise clear state
        clear();

    }, [clear, denormalizePoints, processPoints]);




    useEffect(() => {

        service.onValidationFailure.add(
            id,
            () => {

                /*
                const message = t(T.rejected_notice)

                addMessage(
                    message,
                    true,
                    4000
                );
                */

                showEditor();

                clear();
            }

        );

        service.onValidationSuccess.add(
            id,
            () => {

                // const message = t( T.accepted_notice );

                /*
                addMessage(
                    message,
                    true,
                    4000
                );
                */

                showStage();

            }
        );

        service.onStartedPlaying.add(
            id,
            () => {

                addMessage(
                    "ZAčal jsem přehrávat",
                    true,
                    1000
                );

            }
        );

        service.onStoppedPlaying.add(
            id,
            () => {

                addMessage(
                    "Tvá kresba byla přehrána",
                    true,
                );

                clear();

                showEditor();

            }
        );

        return () => {
            service.onValidationFailure.delete(id);
            service.onValidationSuccess.delete(id);
            service.onStartedPlaying.delete(id);
            service.onStoppedPlaying.delete(id);
        }

    }, [service, clear, showEditor, showStage, addMessage, id, t]);


    return <StrokeContext.Provider value={{
        rawPoints,
        previewPoints,
        finished,
        startStroke,
        addPoint,
        endStroke,
        clear,
        processedBase64,
        processedNormalized,
        restore,
        color,
        setColor,
        availableColors,
        comment,
        setComment,
        setCanvasSize
    }}>
        {children}
    </StrokeContext.Provider>

}

export const useStrokeContext = () => {

    const context = useContext(StrokeContext);
    if (!context) {
        throw new Error("useStrokeContext must be used within a StrokeProvider");
    }

    return context;

}